import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Send, CheckCircle2, ArrowRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function QuizSection() {
  const { lang } = useLang();
  const [answers, setAnswers] = useState({
    investment: null,
    format: null,
    approach: null,
  });
  const [showResult, setShowResult] = useState(false);

  const content = lang === 'ru' ? {
    title: 'Подходит ли вам эта франшиза?',
    subtitle: 'Ответьте на 3 коротких вопроса и получите подходящий формат запуска.',
    
    q1: {
      title: 'Какой уровень инвестиций вы рассматриваете?',
      options: [
        { value: 'basic', label: '100 000 €+' },
        { value: 'standard', label: '150 000 €+' },
        { value: 'premium', label: '300 000 €+' },
      ],
    },
    
    q2: {
      title: 'Какой формат вам ближе?',
      options: [
        { value: 'compact', label: 'Компактный запуск' },
        { value: 'recommended', label: 'Рекомендуемый формат' },
        { value: 'premium', label: 'Масштабный премиум-формат' },
      ],
    },
    
    q3: {
      title: 'Как вы хотите запускаться?',
      options: [
        { value: 'solo', label: 'Самостоятельно' },
        { value: 'partner', label: 'С партнёром' },
        { value: 'exploring', label: 'Пока изучаю модель' },
      ],
    },
    
    result: {
      title: 'Рекомендуемый следующий шаг',
      text: 'Свяжитесь с нами, и мы покажем подходящий формат запуска и условия партнёрства.',
      cta_primary: 'Позвонить',
      cta_secondary: 'Написать в Telegram',
    },
    
    showResult: 'Показать результат',
  } : {
    title: '¿Es esta franquicia adecuada para usted?',
    subtitle: 'Responda 3 preguntas cortas y obtenga el formato de lanzamiento adecuado.',
    
    q1: {
      title: '¿Qué nivel de inversión está considerando?',
      options: [
        { value: 'basic', label: '100 000 €+' },
        { value: 'standard', label: '150 000 €+' },
        { value: 'premium', label: '300 000 €+' },
      ],
    },
    
    q2: {
      title: '¿Qué formato le resulta más cercano?',
      options: [
        { value: 'compact', label: 'Lanzamiento compacto' },
        { value: 'recommended', label: 'Formato recomendado' },
        { value: 'premium', label: 'Formato premium a gran escala' },
      ],
    },
    
    q3: {
      title: '¿Cómo quiere lanzar?',
      options: [
        { value: 'solo', label: 'Independientemente' },
        { value: 'partner', label: 'Con un socio' },
        { value: 'exploring', label: 'Todavía explorando el modelo' },
      ],
    },
    
    result: {
      title: 'Siguiente paso recomendado',
      text: 'Contáctenos y le mostraremos el formato de lanzamiento adecuado y las condiciones de asociación.',
      cta_primary: 'Llamar',
      cta_secondary: 'Escribir por Telegram',
    },
    
    showResult: 'Mostrar resultado',
  };

  const handleSelect = (question, value) => {
    setAnswers(prev => ({ ...prev, [question]: value }));
  };

  const getRecommendation = () => {
    const { investment, format } = answers;
    
    if (lang === 'ru') {
      if (investment === 'basic' || format === 'compact') {
        return 'Вам подходит стартовый формат';
      } else if (investment === 'premium' || format === 'premium') {
        return 'Вам подходит премиальный формат';
      } else {
        return 'Вам подходит рекомендуемый формат';
      }
    } else {
      if (investment === 'basic' || format === 'compact') {
        return 'El formato de inicio es adecuado para usted';
      } else if (investment === 'premium' || format === 'premium') {
        return 'El formato premium es adecuado para usted';
      } else {
        return 'El formato recomendado es adecuado para usted';
      }
    }
  };

  const allAnswered = answers.investment && answers.format && answers.approach;

  const handleShowResult = () => {
    if (allAnswered) {
      setShowResult(true);
    }
  };

  const QuestionCard = ({ question, questionKey, options }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mb-8"
    >
      <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
        {question}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map((option) => (
          <motion.button
            key={option.value}
            onClick={() => handleSelect(questionKey, option.value)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`
              relative p-6 rounded-2xl border-2 transition-all duration-300
              ${answers[questionKey] === option.value
                ? 'border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/20 to-transparent'
                : 'border-white/10 bg-white/5 hover:border-white/20'
              }
            `}
            style={{
              boxShadow: answers[questionKey] === option.value
                ? '0 10px 30px rgba(212,175,55,0.3)'
                : 'none',
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-white font-semibold text-base">
                {option.label}
              </span>
              {answers[questionKey] === option.value && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                >
                  <CheckCircle2 size={20} className="text-[#D4AF37]" />
                </motion.div>
              )}
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );

  return (
    <section className="relative py-20 md:py-28 px-6 overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(212,175,55,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-5xl font-black font-heading gold-text mb-4 leading-tight">
            {content.title}
          </h2>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
            {content.subtitle}
          </p>
        </motion.div>

        {/* Quiz Container */}
        <div className="glass-premium rounded-3xl p-8 md:p-12">
          <AnimatePresence mode="wait">
            {!showResult ? (
              <motion.div
                key="quiz"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Questions */}
                <QuestionCard
                  question={content.q1.title}
                  questionKey="investment"
                  options={content.q1.options}
                />
                <QuestionCard
                  question={content.q2.title}
                  questionKey="format"
                  options={content.q2.options}
                />
                <QuestionCard
                  question={content.q3.title}
                  questionKey="approach"
                  options={content.q3.options}
                />

                {/* Show Result Button */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: allAnswered ? 1 : 0.5 }}
                  className="text-center mt-8"
                >
                  <motion.button
                    onClick={handleShowResult}
                    disabled={!allAnswered}
                    whileHover={allAnswered ? { scale: 1.05 } : {}}
                    whileTap={allAnswered ? { scale: 0.98 } : {}}
                    className={`
                      btn-call flex items-center gap-3 px-10 py-4 rounded-full text-base font-bold mx-auto
                      ${allAnswered ? '' : 'opacity-50 cursor-not-allowed'}
                    `}
                  >
                    {content.showResult}
                    <ArrowRight size={20} />
                  </motion.button>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                {/* Recommendation */}
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-[#D4AF37]/20 to-[#22C55E]/10 border border-[#D4AF37]/30"
                >
                  <h3 className="text-2xl md:text-3xl font-bold gold-text mb-2">
                    {getRecommendation()}
                  </h3>
                </motion.div>

                {/* Next Step */}
                <div className="mb-8">
                  <h4 className="text-xl md:text-2xl font-bold text-white mb-4">
                    {content.result.title}
                  </h4>
                  <p className="text-white/80 text-lg mb-8">
                    {content.result.text}
                  </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <motion.a
                    href="tel:+34624586917"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-call flex items-center gap-3 px-8 py-4 rounded-full text-base font-bold w-full sm:w-auto justify-center min-w-[200px] shadow-2xl"
                  >
                    <Phone size={20} />
                    {content.result.cta_primary}
                  </motion.a>
                  <motion.a
                    href="https://t.me/SerjValencia"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-telegram flex items-center gap-3 px-8 py-4 rounded-full text-base font-bold w-full sm:w-auto justify-center min-w-[200px] shadow-2xl"
                  >
                    <Send size={20} />
                    {content.result.cta_secondary}
                  </motion.a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
